import React from 'react';
/**
 * DjayAvatar part — accessory layer (hand / head / face / neck / effects).
 *
 * This is the highest-churn extension surface: every unlockable cosmetic is a
 * `case` here. Verbatim extraction of the former `renderAccessory()`. To add a
 * new accessory: add its token to `DjayAccessory` (types.ts) + one `case`
 * below. The `uid` keeps gradient ids unique per rendered avatar.
 */
import type { DjayAccessory } from '../types';
interface AccessoryProps {
  accessory: DjayAccessory;
  uid: string;
  showAccessory: boolean;
}
export function Accessory({
  accessory,
  uid,
  showAccessory
}: AccessoryProps) {
  if (!showAccessory) return null;
  switch (accessory) {
    // HAND ACCESSORIES
    case 'seedling':
      return <g className="djay-accessory" transform="translate(165, 110) scale(1.5)" data-id="element-3275" data-mp="w4fn1mkt62qo">
          
          <path d="M0 20 Q-5 10 -15 5 Q-5 15 0 20 Z" fill="#4ADE80" data-id="element-3276" data-mp="2syj2wx3au0zf" />
          <path d="M0 20 Q5 10 15 5 Q5 15 0 20 Z" fill="#22C55E" data-id="element-3277" data-mp="2xvsr69eyc6lo" />
          <path d="M0 20 L0 30" stroke="#166534" strokeWidth="3" strokeLinecap="round" data-id="element-3278" data-mp="2xy5hb9uv5n9t" />
          
        </g>;
    case 'pencil':
      return <g className="djay-accessory" transform="translate(165, 100) rotate(45) scale(1.5)" data-id="element-3279" data-mp="1j295e73uvoy7">
          
          <rect x="-5" y="0" width="10" height="30" fill="#FBBF24" rx="2" data-id="element-3280" data-mp="1e2mptr0k1wm8" />
          <path d="M-5 0 L0 -10 L5 0 Z" fill="#FCA5A5" data-id="element-3281" data-mp="1a22xr1uu9j6y" />
          <path d="M-2 -6 L0 -10 L2 -6 Z" fill="#1E293B" data-id="element-3282" data-mp="1a3nrwqd8r04a" />
          <rect x="-5" y="30" width="10" height="5" fill="#94A3B8" data-id="element-3283" data-mp="26sbv4gkasitn" />
          <rect x="-5" y="35" width="10" height="8" fill="#F87171" rx="2" data-id="element-3284" data-mp="21sz9fz55xhsn" />
        </g>;
    case 'smallFlame':
      return <g className="djay-accessory" transform="translate(165, 110) scale(1.5)" data-id="element-3285" data-mp="1xqvwf62ui559">
          
          <path d="M0 20 C-10 20 -15 10 -5 0 C0 -10 5 -20 0 -25 C10 -15 15 -5 5 5 C10 10 10 20 0 20 Z" fill="#F97316" data-id="element-3286" data-mp="1sskkmpszpkke" />
          
          <path d="M0 15 C-5 15 -8 8 -2 0 C0 -5 2 -10 0 -12 C5 -7 8 -2 2 3 C5 5 5 15 0 15 Z" fill="#FBBF24" data-id="element-3287" data-mp="md9u0j7z671s" />
          
        </g>;
    case 'trophy':
      return <g className="djay-accessory" transform="translate(165, 100) scale(1.5)" data-id="element-3288" data-mp="iub3324g6r8h">
          
          <path d="M-15 0 L15 0 L10 20 L-10 20 Z" fill="#FBBF24" data-id="element-3289" data-mp="ntdyduumlj39" />
          <path d="M-5 20 L5 20 L5 30 L-5 30 Z" fill="#D97706" data-id="element-3290" data-mp="nsvwahla8anf" />
          <path d="M-12 30 L12 30 L12 35 L-12 35 Z" fill="#92400E" data-id="element-3291" data-mp="9ue86irgsw4z" />
          <path d="M-15 5 C-25 5 -25 15 -10 15" fill="none" stroke="#FBBF24" strokeWidth="3" strokeLinecap="round" data-id="element-3292" data-mp="dv7w3c4wqaxv" />
          
          <path d="M15 5 C25 5 25 15 10 15" fill="none" stroke="#FBBF24" strokeWidth="3" strokeLinecap="round" data-id="element-3293" data-mp="3jljdab3uj0" />
          
        </g>;
    case 'starWand':
      return <g className="djay-accessory" transform="translate(165, 100) rotate(15) scale(1.5)" data-id="element-3294" data-mp="4wctsb4zhkzu">
          
          <rect x="-2" y="0" width="4" height="40" fill="#D97706" rx="2" data-id="element-3295" data-mp="3ra49go52embt" />
          <path d="M0 -15 L4 -4 L15 -4 L6 3 L10 14 L0 7 L-10 14 L-6 3 L-15 -4 L-4 -4 Z" fill="#FDE047" data-id="element-3296" data-mp="3rae3cmt8ddms" />
          
          <path d="M0 -10 L2 -3 L9 -3 L4 2 L6 9 L0 4 L-6 9 L-4 2 L-9 -3 L-2 -3 Z" fill="#FEF08A" data-id="element-3297" data-mp="3dcpg58boxuek" />
          
        </g>;
    case 'scepter':
      return <g className="djay-accessory" transform="translate(165, 90) rotate(15) scale(1.5)" data-id="element-3298" data-mp="2yopefb3n6hj6">
          
          <rect x="-3" y="0" width="6" height="50" fill="#FCD34D" rx="3" data-id="element-3299" data-mp="2tpl2wdt8w7po" />
          <circle cx="0" cy="-5" r="12" fill="#8B5CF6" data-id="element-3300" data-mp="37oubarb7lxj3" />
          <circle cx="0" cy="-5" r="8" fill="#A78BFA" data-id="element-3301" data-mp="37m826n8o79yr" />
          <path d="M-15 -5 L15 -5 M0 -20 L0 10" stroke="#FCD34D" strokeWidth="3" data-id="element-3302" data-mp="33mhdqq6m95xe" />
          
          <circle cx="0" cy="-5" r="3" fill="#FDE047" data-id="element-3303" data-mp="3hlqjevx74hn4" />
        </g>;
    case 'cosmicOrb':
      return <g className="djay-accessory" transform="translate(158, 122) scale(1.4)" data-id="element-3304" data-mp="3co77w3rvuiyv">
          
          <circle cx="0" cy="0" r="15" fill={`url(#${uid}-cosmic-orb-grad)`} data-id="element-3305" data-mp="3rctjpviawslc" />
          <circle cx="0" cy="0" r="15" fill="none" stroke="#C084FC" strokeWidth="2" strokeDasharray="5 5" className="djay-spin" data-id="element-3306" data-mp="3md8l5m71mm95" />
          
          <circle cx="-5" cy="-5" r="3" fill="white" fillOpacity="0.8" data-id="element-3307" data-mp="48nhdciljeni" />
          <circle cx="6" cy="4" r="1.5" fill="white" fillOpacity="0.6" data-id="element-3308" data-mp="3vef6q9ojun99" />
          <path d="M-20 0 Q0 -10 20 0 Q0 10 -20 0 Z" fill="#E879F9" fillOpacity="0.4" transform="rotate(30)" data-id="element-3309" data-mp="3vc2ft70td2k2" />
          
          <defs data-id="element-3310" data-mp="1ynkhhitarbly">
            <radialGradient id={`${uid}-cosmic-orb-grad`} cx="30%" cy="30%" r="70%" data-id="element-3311" data-mp="1yn1146jaova1">
              
              <stop offset="0%" stopColor="#E879F9" data-id="element-3312" data-mp="23earxagky4ni" />
              <stop offset="50%" stopColor="#A855F7" data-id="element-3313" data-mp="23gwxj4jwua33" />
              <stop offset="100%" stopColor="#4C1D95" data-id="element-3314" data-mp="27eulk41y2q4w" />
            </radialGradient>
          </defs>
        </g>;
    case 'shadowOrb':
      return <g className="djay-accessory" transform="translate(158, 122) scale(1.4)" data-id="element-3315" data-mp="1as83tql6p71d">
          
          <circle cx="0" cy="0" r="15" fill="#0F172A" data-id="element-3316" data-mp="1fr2lgp7f0pjw" />
          <circle cx="0" cy="0" r="17" fill="none" stroke="#334155" strokeWidth="2" strokeDasharray="8 4" className="djay-spin-reverse" data-id="element-3317" data-mp="1jpieb9q6gs58" />
          
          <circle cx="-4" cy="-4" r="4" fill="#475569" fillOpacity="0.5" data-id="element-3318" data-mp="1ona6m4uqig9y" />
          <path d="M-10 5 Q0 -5 10 5 Q0 15 -10 5 Z" fill="#1E293B" data-id="element-3319" data-mp="2v575kdagsdbu" />
          <circle cx="0" cy="0" r="20" fill="none" stroke="#EF4444" strokeWidth="1" strokeOpacity="0.5" data-id="element-3320" data-mp="3040edset3g5o" />
          
        </g>;

    // HEAD ACCESSORIES
    case 'doubleFlameCrown':
      return <g className="djay-accessory" transform="translate(100, 15) scale(1.5)" data-id="element-3321" data-mp="3595eng3nnxth">
          <path d="M-20 20 L-10 0 L0 15 L10 0 L20 20 Z" fill="#F59E0B" data-id="element-3322" data-mp="30ioyvpcd403y" />
          <path d="M-15 20 L-10 5 L0 15 L10 5 L15 20 Z" fill="#FCD34D" data-id="element-3323" data-mp="30guau25snrvn" />
          <circle cx="-10" cy="-2" r="3" fill="#EF4444" data-id="element-3324" data-mp="2wgk4goe3azg9" />
          <circle cx="10" cy="-2" r="3" fill="#EF4444" data-id="element-3325" data-mp="2rhz8x29s93y5" />
        </g>;
    case 'cap':
      return <g className="djay-accessory" transform="translate(100, 25) scale(1.4)" data-id="element-3326" data-mp="2mj5u96irf6we">
          <path d="M-25 10 C-25 -10 25 -10 25 10 Z" fill="#3B82F6" data-id="element-3327" data-mp="2mliig1f42dhw" />
          <path d="M-35 10 L30 10" stroke="#2563EB" strokeWidth="4" strokeLinecap="round" data-id="element-3328" data-mp="2hkl1tt9r1rii" />
          
          <circle cx="0" cy="-5" r="3" fill="#60A5FA" data-id="element-3329" data-mp="2dk297x018hf9" />
        </g>;

    // FACE ACCESSORIES
    case 'sunglasses':
      return <g className="djay-accessory" transform="translate(100, 95) scale(1.4)" data-id="element-3330" data-mp="fqlpd4kw5448">
          <rect x="-35" y="-10" width="30" height="20" rx="4" fill="#0F172A" data-id="element-3331" data-mp="brw5qu99eqc5" />
          <rect x="5" y="-10" width="30" height="20" rx="4" fill="#0F172A" data-id="element-3332" data-mp="2591ubwpz5mg3" />
          <path d="M-5 -5 L5 -5" stroke="#0F172A" strokeWidth="4" data-id="element-3333" data-mp="2a13bnx2023cz" />
          <path d="M-35 -5 L-45 -10" stroke="#0F172A" strokeWidth="4" strokeLinecap="round" data-id="element-3334" data-mp="2a2ekwce7s3bh" />
          
          <path d="M35 -5 L45 -10" stroke="#0F172A" strokeWidth="4" strokeLinecap="round" data-id="element-3335" data-mp="20aqkvn3lddye" />
          
          {/* Reflections */}
          <path d="M-30 -5 L-15 -5" stroke="#F8FAFC" strokeWidth="2" strokeLinecap="round" opacity="0.5" data-id="element-3336" data-mp="2o033teoq10dw" />
          
          <path d="M10 -5 L25 -5" stroke="#F8FAFC" strokeWidth="2" strokeLinecap="round" opacity="0.5" data-id="element-3337" data-mp="2sy4o22xvv5qy" />
          
        </g>;

    // NECK ACCESSORIES
    case 'badge100':
      return <g className="djay-accessory" transform="translate(100, 162) scale(1.3)" data-id="element-3338" data-mp="2jxybcja0e9su">
          
          <path d="M-20 -20 L0 0 L20 -20" fill="none" stroke="#3B82F6" strokeWidth="6" data-id="element-3339" data-mp="13k863975lfhm" />
          
          <circle cx="0" cy="0" r="18" fill="#EF4444" data-id="element-3340" data-mp="18juixhj2ktpw" />
          <circle cx="0" cy="0" r="14" fill="none" stroke="#FEF08A" strokeWidth="2" strokeDasharray="4 2" data-id="element-3341" data-mp="18hhuqmmpxn4e" />
          
          <text x="0" y="5" fontFamily="sans-serif" fontSize="14" fontWeight="bold" fill="white" textAnchor="middle" data-id="element-3342" data-mp="w0ytz879j2ov">
            
            100
          </text>
        </g>;
    case 'medal':
      return <g className="djay-accessory" transform="translate(100, 162) scale(1.3)" data-id="element-3343" data-mp="151n8s54hd72t">
          
          <path d="M-20 -30 L0 0 L20 -30" fill="none" stroke="#3B82F6" strokeWidth="6" data-id="element-3344" data-mp="hc2ggvmfsxgv" />
          
          <path d="M-15 -30 L0 0 L15 -30" fill="none" stroke="#60A5FA" strokeWidth="2" data-id="element-3345" data-mp="cczmq7bwqdo7" />
          
          <circle cx="0" cy="5" r="16" fill="#F59E0B" data-id="element-3346" data-mp="r1vqvtauf6jj" />
          <circle cx="0" cy="5" r="12" fill="#FCD34D" data-id="element-3347" data-mp="r3glfix5qphe" />
          <text x="0" y="10" fontFamily="sans-serif" fontSize="12" fontWeight="bold" fill="#92400E" textAnchor="middle" data-id="element-3348" data-mp="mangatxs1f4s">
            
            1
          </text>
        </g>;
    case 'leafGarland':
      return <g className="djay-accessory" transform="translate(100, 130) scale(1.3)" data-id="element-3349" data-mp="3pse26wlerxhz">
          
          <path d="M-45 -10 Q0 30 45 -10" fill="none" stroke="#22C55E" strokeWidth="4" data-id="element-3350" data-mp="3492r3e9lw5z0" />
          
          <path d="M-35 10 Q-40 5 -35 0 Q-30 5 -35 10 Z" fill="#4ADE80" data-id="element-3351" data-mp="39748zvvapz8w" />
          <path d="M-15 18 Q-20 13 -15 8 Q-10 13 -15 18 Z" fill="#4ADE80" data-id="element-3352" data-mp="399gyqv7apdwi" />
          <path d="M5 20 Q0 15 5 10 Q10 15 5 20 Z" fill="#4ADE80" data-id="element-3353" data-mp="3e5fl6d92clua" />
          <path d="M25 18 Q20 13 25 8 Q30 13 25 18 Z" fill="#4ADE80" data-id="element-3354" data-mp="3j6v49zrokia1" />
          <path d="M40 8 Q35 3 40 -2 Q45 3 40 8 Z" fill="#4ADE80" data-id="element-3355" data-mp="3n7oreqxatt1v" />
        </g>;
    case 'goldChain':
      return <g className="djay-accessory" transform="translate(100, 158) scale(1.4)" data-id="element-3356" data-mp="3s60213vf47l5">
          
          <path d="M-30 -10 Q0 30 30 -10" fill="none" stroke="#F59E0B" strokeWidth="6" strokeLinecap="round" data-id="element-3357" data-mp="s40elbntjbk" />
          
          <path d="M-30 -10 Q0 30 30 -10" fill="none" stroke="#FCD34D" strokeWidth="2" strokeLinecap="round" strokeDasharray="4 2" data-id="element-3358" data-mp="5pvtvjryddhv" />
          
          <circle cx="0" cy="10" r="8" fill="#F59E0B" data-id="element-3359" data-mp="anxfocgrjqx1" />
          <circle cx="0" cy="10" r="5" fill="#FCD34D" data-id="element-3360" data-mp="esn81rc7qnd7" />
        </g>;

    // EFFECTS (Background/Surrounding)
    case 'tripleStars':
      return <g className="djay-accessory" transform="translate(100, 20) scale(1.5)" data-id="element-3361" data-mp="1z7jna79ebw5c">
          <g transform="translate(-35, 0) scale(0.7)" data-id="element-3362" data-mp="1v6hp7tr7tq4y">
            <path d="M0 -15 L4 -4 L15 -4 L6 3 L10 14 L0 7 L-10 14 L-6 3 L-15 -4 L-4 -4 Z" fill="#FDE047" data-id="element-3363" data-mp="1q8g3f12encps" />
            
          </g>
          <g transform="translate(0, -15)" data-id="element-3364" data-mp="1lb6gbs2hnd54">
            <path d="M0 -15 L4 -4 L15 -4 L6 3 L10 14 L0 7 L-10 14 L-6 3 L-15 -4 L-4 -4 Z" fill="#FDE047" data-id="element-3365" data-mp="2ixnd8w7b7oqc" />
            
          </g>
          <g transform="translate(35, 0) scale(0.7)" data-id="element-3366" data-mp="2dyagnjqmz3ak">
            <path d="M0 -15 L4 -4 L15 -4 L6 3 L10 14 L0 7 L-10 14 L-6 3 L-15 -4 L-4 -4 Z" fill="#FDE047" data-id="element-3367" data-mp="2e0wnfh5pderq" />
            
          </g>
        </g>;
    default:
      return null;
  }
}